import ReCAPTCHA from "react-google-recaptcha";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import axios, { AxiosRequestConfig } from "axios";
import { useEffect, useState } from "react";
import { useMediaQuery } from "utils/useMediaQuery";

const emailRegex = /^\s*(?:[\w!#$%&'*+\-\/=?^`{|}~]+\.)*[\w!#$%&'*+\-\/=?^`{|}~]+@(?:(?:(?:[a-zA-Z0-9](?:[a-zA-Z0-9\-](?!\.)){0,61}[a-zA-Z0-9]?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9\-](?!$)){0,61}[a-zA-Z0-9]?)|(?:\[(?:(?:[01]?\d{1,2}|2[0-4]\d|25[0-5])\.){3}(?:[01]?\d{1,2}|2[0-4]\d|25[0-5])\]))\s*$/;
const phoneRegex = /^\+[0-9]{8,15}$/;

interface IContactMessage {
    name: string;
    email: string;
    phone: string;
    subject: string;
    question: string;
    recaptcha: string;
}

export interface IEmailRequestBody {
    subject: string;
    text: string;
    recaptchaToken: string;
}

function generateEmail(name: string, email: string, phone: string, subject: string, message: string): string {
    return (
        `<!DOCTYPE html>
    <html lang="en">
    <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>New Message Received</title>
        <style>
            body {
                font-family: Arial, sans-serif;
                line-height: 1.6;
            }
            .container {
                margin: 20px;
                padding: 20px;
                border: 1px solid #ddd;
                border-radius: 5px;
            }
            .header {
                font-size: 20px;
                font-weight: bold;
                margin-bottom: 20px;
            }
            .details {
                margin-bottom: 10px;
            }
            .details p {
                margin: 5px 0;
            }
            .footer {
                margin-top: 20px;
            }
        </style>
    </head>
    <body>
        <div class="container">
            <div class="header">New Message Received</div>
            <div class="details">
                <p><strong>Name:</strong> ${name}</p>
                <p><strong>Email:</strong> ${email}</p>
                <p><strong>Phone:</strong> ${phone}</p>
                <p><strong>Subject:</strong> ${subject}</p>
                <p><strong>Message:</strong></p>
                <p>${message}</p>
            </div>
            
        </div>
    </body>
    </html>
    `
    );
}

const schema = yup.object({
    name: yup.string().required("Name is required"),
    email: yup.string().email("Invalid email").required("Email is required").matches(emailRegex, "Invalid email"),
    phone: yup.string().notRequired().matches(phoneRegex, {
        message: "Invalid phone number",
        excludeEmptyString: true,
    }),
    subject: yup.string().required("Subject is required"),
    question: yup.string().required("Message is required"),
    recaptcha: yup.string().required("Please complete the reCAPTCHA"),
});

const defaultValues = {
    name: "",
    email: "",
    phone: "",
    subject: "",
    question: "",
    recaptcha: "",
};

const LetsTalk: React.FC = () => {
    const isMobile = useMediaQuery("(max-width: 800px)");
    const [successVisible, setSuccessVisible] = useState(false);

    const formMethods = useForm<IContactMessage>({
        defaultValues: defaultValues,
        resolver: yupResolver(schema as any),
    });

    const {
        control,
        reset,
        handleSubmit,
        formState: { isDirty },
    } = formMethods;

    const onSubmit = (data: IContactMessage) => {
        const emailRequestBody: IEmailRequestBody = {
            subject: `Aghvida Website Message From ${data.name}`,
            text: generateEmail(data.name, data.email, data.phone, data.subject, data.question),
            recaptchaToken: data.recaptcha,
        };
        const config: AxiosRequestConfig = {
            method: "post",
            maxBodyLength: Infinity,
            url: process.env.REACT_APP_EMAIL_API_URL,
            headers: {
                "Content-Type": "application/json",
            },
            data: JSON.stringify(emailRequestBody),
        };
        axios
            .request(config)
            .then((response) => {
                setSuccessVisible(true);
            })
            .catch((error) => {
                console.log("Error sending email: ", error);
            });
    };
    const onInvalid = (errors: any) => {
        console.log("Form Validation Errors: ", errors);
    };

    return (
        <>
            <section>
                <picture data-position="absolute" data-top="0" data-left="0" data-index="-1" data-mask="">
                    <img src="./images/S10.png" alt="" />
                </picture>
                <group data-direction="column">
                    <space data-height="80" data-adaptive="desktop"></space>
                    <space data-height="80" data-adaptive="mobile"></space>
                    <group data-wrap="no">
                        <space data-length="50" data-adaptive="desktop"></space>
                        <space data-length="30" data-adaptive="mobile"></space>
                        <wrapper data-align="center">
                            <group className="article" data-margin="auto" data-direction="column" data-gap="50"
                                data-align="center">
                                <text data-wrap="wrap" data-weight="600" data-type="group" className="title"
                                    data-direction="column" data-text-size="xxx-large" data-align="center">
                                    <text data-wrap="wrap">Let’s Talk</text>
                                </text>
                                <text className="description" data-wrap="wrap" data-line="2" data-space-vertical="5"
                                    data-max-length="700" data-text-align="center">
                                    We’d love to hear from you for any question or feedback. Simply fill out the form
                                    below, and our team will get back to you as quickly as possible.
                                </text>
                                <group data-wrap="no" data-radius="10" data-justify="center" data-gap="20" data-max-length="500" data-length="forcefit">
                                    <group data-opacity="30" data-width="auto" data-align="start"> <text data-type="group" data-text-size="64" data-line-height="50" data-height="20">“</text></group>
                                    <group data-direction="column" data-gap="10">
                                        <text className="list_description" data-wrap="wrap" data-text-size="14" data-line="2" data-text-align="justify" data-color="black" data-weight="700">Your best, healthiest self does need a template- start crafting it today!</text>
                                    </group>
                                    <group data-opacity="30" data-width="auto" data-align="end"><text data-rotate="180" data-type="group" data-text-size="64" data-line-height="50" data-height="20">“</text></group>
                                </group>
                                <group data-direction="column">
                                    <group data-gap="40">
                                        <group data-length="forcefit" data-direction="column"
                                            data-space="40" data-max-length="600" data-backdrop="" data-radius="10"
                                            data-elevation="1">
                                            <fields data-type="material" data-length="forcefit" data-max-length="350" >
                                                <Controller
                                                    control={control}
                                                    name="name"
                                                    render={({
                                                        field: { onChange, onBlur, value, name, ref },
                                                        fieldState: { invalid, isTouched, isDirty, error }, }) => (
                                                        <>
                                                            <fieldwrap>
                                                                <decoration>
                                                                    <line data-name="left"></line>
                                                                    <group data-wrap="no" data-length="auto">
                                                                        <wrap data-fit="1">
                                                                            <group data-wrap="no" data-height="fit"
                                                                                data-width="auto"><label data-height="fit">
                                                                                    <group data-height="fit" data-align="center"
                                                                                        data-width="auto" data-name="label_wrap">
                                                                                        <line data-name="top"></line>
                                                                                        <group data-height="fit" data-align="center"
                                                                                            data-width="auto" data-wrap="no"
                                                                                            data-name="label"><text>Your Name
                                                                                                *</text>
                                                                                        </group>
                                                                                    </group>
                                                                                    <group data-direction="column" data-height="fit"
                                                                                        data-fit="1">
                                                                                        <line data-name="top secondary"></line>
                                                                                    </group>
                                                                                </label></group>
                                                                            <line data-name="bottom"></line>
                                                                        </wrap>
                                                                    </group>
                                                                    <line data-name="right"></line>
                                                                </decoration>
                                                                <wrap data-type="group" data-gap="5" data-wrap="" data-height="fit"
                                                                    data-length="autofit" data-contain="">
                                                                    <input
                                                                        type="text"
                                                                        placeholder=""
                                                                        required
                                                                        onBlur={onBlur}
                                                                        onChange={onChange}
                                                                        value={value || ''}
                                                                        ref={ref} />

                                                                </wrap>
                                                                <group data-name="hide" data-width="auto"></group>
                                                            </fieldwrap>
                                                            <group data-align="center" data-wrap="no" data-name="label" className="input-validation-error">
                                                                <text data-name="validation_text">
                                                                    {error ? error.message : ""}
                                                                </text>
                                                            </group>
                                                        </>
                                                    )} />

                                            </fields>

                                            <fields data-type="material" data-length="forcefit" data-max-length="450">
                                                <Controller
                                                    control={control}
                                                    name="email"
                                                    render={({
                                                        field: { onChange, onBlur, value, name, ref },
                                                        fieldState: { invalid, isTouched, isDirty, error }, }) => (
                                                        <>
                                                            <fieldwrap>
                                                                <decoration>
                                                                    <line data-name="left"></line>
                                                                    <group data-wrap="no" data-length="auto">
                                                                        <wrap data-fit="1">
                                                                            <group data-wrap="no" data-height="fit"
                                                                                data-width="auto"><label data-height="fit">
                                                                                    <group data-height="fit" data-align="center"
                                                                                        data-width="auto" data-name="label_wrap">
                                                                                        <line data-name="top"></line>
                                                                                        <group data-height="fit" data-align="center"
                                                                                            data-width="auto" data-wrap="no"
                                                                                            data-name="label"><text>Your Email
                                                                                                *</text>
                                                                                        </group>
                                                                                    </group>
                                                                                    <group data-direction="column" data-height="fit"
                                                                                        data-fit="1">
                                                                                        <line data-name="top secondary"></line>
                                                                                    </group>
                                                                                </label></group>
                                                                            <line data-name="bottom"></line>
                                                                        </wrap>
                                                                    </group>
                                                                    <line data-name="right"></line>
                                                                </decoration>
                                                                <wrap data-type="group" data-gap="5" data-wrap="" data-height="fit"
                                                                    data-length="autofit" data-contain="">
                                                                    <input className=""
                                                                        placeholder=""
                                                                        type="text"
                                                                        onBlur={onBlur}
                                                                        onChange={onChange}
                                                                        value={value || ''}
                                                                        ref={ref} />
                                                                </wrap>
                                                                <group data-name="hide" data-width="auto"></group>
                                                            </fieldwrap>
                                                            <group data-align="center" data-wrap="no" data-name="label" className="input-validation-error">
                                                                <text data-name="validation_text">
                                                                    {error ? error.message : ""}
                                                                </text>
                                                            </group>
                                                        </>
                                                    )} />
                                            </fields>

                                            <fields data-type="material" data-length="forcefit">
                                                <Controller
                                                    control={control}
                                                    name="phone"
                                                    render={({
                                                        field: { onChange, onBlur, value, name, ref },
                                                        fieldState: { invalid, isTouched, isDirty, error }, }) => (
                                                        <>
                                                            <fieldwrap>
                                                                <decoration>
                                                                    <line data-name="left"></line>
                                                                    <group data-wrap="no" data-length="auto">
                                                                        <wrap data-fit="1">
                                                                            <group data-wrap="no" data-height="fit"
                                                                                data-width="auto"><label data-height="fit">
                                                                                    <group data-height="fit" data-align="center"
                                                                                        data-width="auto" data-name="label_wrap">
                                                                                        <line data-name="top"></line>
                                                                                        <group data-height="fit" data-align="center"
                                                                                            data-width="auto" data-wrap="no"
                                                                                            data-name="label"><text>Your Phone</text>
                                                                                        </group>
                                                                                    </group>
                                                                                    <group data-direction="column" data-height="fit"
                                                                                        data-fit="1">
                                                                                        <line data-name="top secondary"></line>
                                                                                    </group>
                                                                                </label></group>
                                                                            <line data-name="bottom"></line>
                                                                        </wrap>
                                                                    </group>
                                                                    <line data-name="right"></line>
                                                                </decoration>
                                                                <wrap data-type="group" data-gap="5" data-wrap="" data-height="fit"
                                                                    data-length="autofit" data-contain="">
                                                                    <input className=""
                                                                        placeholder=""
                                                                        type="text"
                                                                        onBlur={onBlur}
                                                                        onChange={onChange}
                                                                        value={value || ''}
                                                                        ref={ref} />
                                                                </wrap>
                                                                <group data-name="hide" data-width="auto"></group>
                                                            </fieldwrap>
                                                            <group data-align="center" data-wrap="no" data-name="label" className="input-validation-error">
                                                                <text data-name="validation_text">
                                                                    {error ? error.message : ""}
                                                                </text>
                                                            </group>
                                                        </>
                                                    )} />
                                            </fields>

                                            <fields data-type="material" data-length="forcefit" data-max-length="300">
                                                <Controller
                                                    control={control}
                                                    name="subject"
                                                    render={({
                                                        field: { onChange, onBlur, value, name, ref },
                                                        fieldState: { invalid, isTouched, isDirty, error }, }) => (
                                                        <>
                                                            <fieldwrap>
                                                                <decoration>
                                                                    <line data-name="left"></line>
                                                                    <group data-wrap="no" data-length="auto">
                                                                        <wrap data-fit="1">
                                                                            <group data-wrap="no" data-height="fit"
                                                                                data-width="auto"><label data-height="fit">
                                                                                    <group data-height="fit" data-align="center"
                                                                                        data-width="auto" data-name="label_wrap">
                                                                                        <line data-name="top"></line>
                                                                                        <group data-height="fit" data-align="center"
                                                                                            data-width="auto" data-wrap="no"
                                                                                            data-name="label"><text>Subject</text>
                                                                                        </group>
                                                                                    </group>
                                                                                    <group data-direction="column" data-height="fit"
                                                                                        data-fit="1">
                                                                                        <line data-name="top secondary"></line>
                                                                                    </group>
                                                                                </label></group>
                                                                            <line data-name="bottom"></line>
                                                                        </wrap>
                                                                    </group>
                                                                    <line data-name="right"></line>
                                                                </decoration>
                                                                <wrap data-type="group" data-gap="5" data-wrap="" data-height="fit"
                                                                    data-length="autofit" data-contain="">
                                                                    <input className=""
                                                                        placeholder=""
                                                                        type="text"
                                                                        onBlur={onBlur}
                                                                        onChange={onChange}
                                                                        value={value || ''}
                                                                        ref={ref} />
                                                                </wrap>
                                                                <group data-name="hide" data-width="auto"></group>
                                                            </fieldwrap>
                                                            <group data-align="center" data-wrap="no" data-name="label" className="input-validation-error">
                                                                <text data-name="validation_text">
                                                                    {error ? error.message : ""}
                                                                </text>
                                                            </group>
                                                        </>
                                                    )} />
                                            </fields>
                                            <fields data-type="material" data-length="forcefit">
                                                <Controller
                                                    control={control}
                                                    name="question"
                                                    render={({
                                                        field: { onChange, onBlur, value, name, ref },
                                                        fieldState: { invalid, isTouched, isDirty, error }, }) => (
                                                        <>
                                                            <fieldwrap>
                                                                <decoration>
                                                                    <line data-name="left"></line>
                                                                    <group data-wrap="no" data-length="auto">
                                                                        <wrap data-fit="1">
                                                                            <group data-wrap="no" data-height="fit"
                                                                                data-width="auto"><label data-height="fit">
                                                                                    <group data-height="fit" data-align="center"
                                                                                        data-width="auto" data-name="label_wrap">
                                                                                        <line data-name="top"></line>
                                                                                        <group data-height="fit" data-align="center"
                                                                                            data-width="auto" data-wrap="no"
                                                                                            data-name="label"><text>Your Question
                                                                                                *</text>
                                                                                        </group>
                                                                                    </group>
                                                                                    <group data-direction="column" data-height="fit"
                                                                                        data-fit="1">
                                                                                        <line data-name="top secondary"></line>
                                                                                    </group>
                                                                                </label>
                                                                            </group>
                                                                            <line data-name="bottom"></line>
                                                                        </wrap>
                                                                    </group>
                                                                    <line data-name="right"></line>
                                                                </decoration>
                                                                <wrap data-type="group" data-gap="5" data-wrap="" data-height="fit"
                                                                    data-length="autofit" data-contain="">
                                                                    <textarea className=""
                                                                        data-height="70"
                                                                        data-resize="none"
                                                                        aria-invalid="false"
                                                                        placeholder=""
                                                                        onBlur={onBlur}
                                                                        onChange={onChange}
                                                                        value={value || ''}
                                                                        ref={ref} />
                                                                </wrap>
                                                                <group data-name="hide" data-width="auto"></group>
                                                            </fieldwrap>
                                                            <group data-align="center" data-wrap="no" data-name="label" className="input-validation-error">
                                                                <text data-name="validation_text">
                                                                    {error ? error.message : ""}
                                                                </text>
                                                            </group>
                                                        </>
                                                    )} />
                                            </fields>


                                            <fields data-type="material" data-length="forcefit">
                                                <Controller name="recaptcha"
                                                    control={control}
                                                    render={({ field, fieldState }) => (
                                                        <>
                                                            <fieldwrap>
                                                                <ReCAPTCHA
                                                                    key={isMobile ? "compact" : "normal"}
                                                                    sitekey={process.env.REACT_APP_ReCAPTCHA_SITE_KEY || ""}
                                                                    onChange={field.onChange}
                                                                    size={isMobile ? "compact" : "normal"}
                                                                />
                                                            </fieldwrap>

                                                            <group data-align="center" data-wrap="no" data-name="label" className="input-validation-error">
                                                                <text data-name="validation_text">
                                                                    {fieldState.error?.message ? fieldState.error?.message : ""}
                                                                </text>
                                                            </group>
                                                        </>
                                                    )} />
                                            </fields>

                                            {successVisible && <message>Message has been sent to our team. We will be in contact with you shortly.</message>}
                                            <space data-height="15"></space>
                                            <group data-justify="center" className="section_toolbar">
                                                <button className="button primary large" data-min-length="200" type="submit" onClick={handleSubmit(onSubmit, onInvalid)}>
                                                    <text data-disabled="">Submit</text>
                                                </button>
                                            </group>
                                        </group>
                                        <group data-length="forcefit" data-fit="1"></group>
                                    </group>
                                </group>
                            </group>
                        </wrapper>
                        <space data-length="50" data-adaptive="desktop"></space>
                        <space data-length="30" data-adaptive="mobile"></space>
                    </group>
                    <space data-height="80"></space>
                </group>

            </section>
        </>
    );
};

export default LetsTalk;